/// <reference lib="dom" />
// ^ The DOM lib is deliberately absent from tsconfig (`@react-native/typescript-config`
//   lists only ES libs) so native code cannot reach for `window` and have it type-check.
//   A `.web.ts` file is the one place those globals genuinely exist, so it opts in here
//   rather than the whole project opting in for it.

/**
 * Turning an API response into a file the user actually has — WEB build.
 *
 * The naive version of this is `window.open(url)`, and it does not work here for a
 * reason that is easy to miss: every export route is authenticated. A new tab is a
 * fresh navigation carrying no `Authorization` header, and on native it carries no
 * session at all — so the download either 401s or silently returns the login page as
 * an .xlsx. The response therefore has to be fetched through the app's own HTTP client
 * (which attaches the credential AND can refresh an expired one mid-download) and only
 * then handed to the browser as a blob.
 *
 * `URL.revokeObjectURL` is not optional either: an un-revoked blob URL pins the whole
 * file in memory for the life of the tab, and a manager exporting a year of attendance
 * a few times over will feel it.
 */

/** A sensible filename when the server did not suggest one. */
function fallbackName(contentType: string | null): string {
  if (contentType?.includes('csv')) {
    return 'samvision.csv';
  }
  if (contentType?.includes('spreadsheet') || contentType?.includes('excel')) {
    return 'samvision.xlsx';
  }
  return 'samvision.dat';
}

/**
 * The server's own suggested filename, if it sent one.
 *
 * Both spellings are read: `filename*=UTF-8''…` (RFC 5987, percent-encoded — the one
 * that survives Cyrillic, which every department name here is) takes precedence over
 * the plain `filename="…"`, which is latin-1 only and arrives mojibake'd when a report
 * is named «Главный отдел».
 */
function nameFromDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-escape is not worth failing a download over.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/**
 * Save a fetched response as a file.
 *
 * @param res         The response from one of the `/…/export` routes.
 * @param suggestedName Used when the server sent no Content-Disposition. Pass something
 *                    the user will recognise — «Посещаемость — Главный отдел — 2026-08».
 */
export async function saveResponseAsFile(
  res: Response,
  suggestedName?: string,
): Promise<void> {
  const blob = await res.blob();
  const name =
    nameFromDisposition(res.headers.get('content-disposition')) ??
    suggestedName ??
    fallbackName(res.headers.get('content-type'));

  saveBlob(blob, name);
}

/** Save bytes the app produced itself — the client-side CSV exports. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  // Must be in the document for Firefox, which ignores a click on a detached node.
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking synchronously can cancel the download in Safari; one turn is enough.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Whether this build can save files at all. Web can; see download.ts for native. */
export const CAN_DOWNLOAD = true;
